import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import RecurringModal from '../components/RecurringModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { FiEdit2, FiTrash2, FiPause, FiPlay } from 'react-icons/fi';
import Header from '../components/Header';
import { toast } from 'react-toastify';
import { CURRENCY_SYMBOLS_TEXT } from '../utils/currencySymbols';
import { FaInfoCircle } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import { motion, AnimatePresence } from 'framer-motion';

const Recurring = () => {
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [confirmAction, setConfirmAction] = useState('');
  const { currentUser } = useAuth();
  const [categories, setCategories] = useState([]);
  const [userCurrencies, setUserCurrencies] = useState(['GBP']); // Default currency
  const [currentView, setCurrentView] = useState('active');

  const getTodayDate = () => {
    const today = new Date();
    today.setDate(today.getDate());
    return today.toISOString().split('T')[0];
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Fetch settings
        const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
        if (settingsDoc.exists()) {
          const { currencies } = settingsDoc.data();
          setUserCurrencies(currencies);
        }

        // Fetch categories
        const categoriesRef = doc(db, 'categories', currentUser.uid);
        const categoriesDoc = await getDoc(categoriesRef);
        if (categoriesDoc.exists()) {
          setCategories(categoriesDoc.data().items || []);
        } else {
          await setDoc(categoriesRef, { items: [] });
          setCategories([]);
        }

        // Fetch recurring expenses
        fetchRecurringExpenses();
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load data');
      }
    };

    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser]);

  const fetchRecurringExpenses = async () => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'recurringExpenses'),
      where('userId', '==', currentUser.uid)
    );
    const querySnapshot = await getDocs(q);
    const expenses = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setRecurringExpenses(expenses);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filterExpenses = (expenses) => {
    return expenses.reduce((acc, expense) => {
      if (expense.endDate) {
        const endDate = new Date(expense.endDate);
        endDate.setHours(0, 0, 0, 0);
        
        if (endDate <= today) {
          acc.completed.push(expense);
          return acc;
        }
      }
      
      if (expense.isPaused) {
        acc.paused.push(expense);
      } else {
        acc.active.push(expense);
      }
      return acc;
    }, { active: [], paused: [], completed: [] });
  };

  const { active: activeExpenses, paused: pausedExpenses, completed: completedExpenses } = filterExpenses(recurringExpenses);

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setIsModalOpen(true);
  };

  const handlePauseConfirm = (expense) => {
    togglePause(expense);
  };

  const handleDeleteConfirm = (expense) => {
    setSelectedExpense(expense);
    setConfirmAction('delete');
    setIsConfirmModalOpen(true);
  };

  const handleConfirmAction = async () => {
    try {
      if (confirmAction === 'delete') {
        await deleteDoc(doc(db, 'recurringExpenses', selectedExpense.id));
        toast.success('Recurring expense deleted successfully');
      } else if (confirmAction === 'pause') {
        await updateDoc(doc(db, 'recurringExpenses', selectedExpense.id), {
          isPaused: true
        });
        toast.success('Recurring expense paused successfully');
      }
      setIsConfirmModalOpen(false);
      fetchRecurringExpenses();
    } catch (error) {
      console.error(`Error ${confirmAction}ing recurring expense:`, error);
      toast.error(`Failed to ${confirmAction} recurring expense`);
    }
  };

  const updateCategories = async (newCategories) => {
    try {
      await setDoc(doc(db, 'categories', currentUser.uid), {
        items: newCategories
      });
      setCategories(newCategories);
    } catch (error) {
      console.error('Error updating categories:', error);
      toast.error('Failed to save category');
    }
  };

  const calculatePaymentDetails = (expense) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(expense.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    // Handle COMPLETED status for nextPaymentDate
    let nextPaymentDate;
    if (expense.isCompleted || expense.nextPaymentDate === "COMPLETED") {
      nextPaymentDate = "COMPLETED";
    } else {
      nextPaymentDate = new Date(expense.nextPaymentDate);
      nextPaymentDate.setHours(0, 0, 0, 0);
    }
    
    const amount = Number(expense.amount);
    const originalAmount = Number(expense.originalAmount || expense.amount);
    
    // For one-off payments or when period is null
    if (expense.isOneOff || !expense.period) {
      return {
        totalAmount: amount,
        amountPaid: startDate <= today ? amount : 0,
        remainingAmount: startDate <= today ? 0 : amount,
        paymentsMade: startDate <= today ? 1 : 0,
        totalPayments: 1,
        nextPaymentDate: startDate > today ? startDate : null,
        originalAmount: originalAmount,
        isOneOff: true
      };
    }

    const periodInMs = (() => {
      switch (expense.period.unit) {
        case 'day': return expense.period.number * 24 * 60 * 60 * 1000;
        case 'week': return expense.period.number * 7 * 24 * 60 * 60 * 1000;
        case 'month': return expense.period.number * 30.44 * 24 * 60 * 60 * 1000; // More accurate month length
        case 'year': return expense.period.number * 365.25 * 24 * 60 * 60 * 1000; // Account for leap years
        default: return expense.period.number * 24 * 60 * 60 * 1000;
      }
    })();

    // Calculate payments made so far
    let paymentsMade = 0;
    if (startDate <= today) {
      const timeDiff = today.getTime() - startDate.getTime();
      paymentsMade = Math.floor(timeDiff / periodInMs) + 1;
    }

    // If expense is completed, set max payments
    if (expense.isCompleted || expense.nextPaymentDate === "COMPLETED") {
      // For expenses with an end date, calculate total payments
      if (expense.endDate) {
        const endDate = new Date(expense.endDate);
        endDate.setHours(23, 59, 59, 999);
        
        // Calculate total number of payments by counting the actual payment dates
        let currentDate = new Date(startDate);
        let totalPayments = 1; // Start with 1 for the initial payment
        
        while (currentDate < endDate) {
          // Add one period
          if (expense.period.unit === 'month') {
            currentDate.setMonth(currentDate.getMonth() + Number(expense.period.number));
          } else if (expense.period.unit === 'year') {
            currentDate.setFullYear(currentDate.getFullYear() + Number(expense.period.number));
          } else {
            currentDate = new Date(currentDate.getTime() + periodInMs);
          }
          
          if (currentDate <= endDate) {
            totalPayments++;
          }
        }
        
        const amountPaid = totalPayments * originalAmount;
        
        return {
          totalAmount: amountPaid,
          amountPaid: amountPaid,
          remainingAmount: 0,
          paymentsMade: totalPayments,
          totalPayments: totalPayments,
          nextPaymentDate: "COMPLETED", // Use the string directly
          originalAmount
        };
      } else {
        // If no end date but marked completed, just return current payments
        const amountPaid = paymentsMade * originalAmount;
        
        return {
          totalAmount: amountPaid,
          amountPaid: amountPaid,
          remainingAmount: 0,
          paymentsMade: paymentsMade,
          totalPayments: paymentsMade,
          nextPaymentDate: "COMPLETED", // Use the string directly
          originalAmount
        };
      }
    }

    // For expenses with an end date
    if (expense.endDate) {
      const endDate = new Date(expense.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      // Calculate total number of payments by counting the actual payment dates
      let currentDate = new Date(startDate);
      let totalPayments = 1; // Start with 1 for the initial payment
      
      while (currentDate < endDate) {
        // Add one period
        if (expense.period.unit === 'month') {
          currentDate.setMonth(currentDate.getMonth() + Number(expense.period.number));
        } else if (expense.period.unit === 'year') {
          currentDate.setFullYear(currentDate.getFullYear() + Number(expense.period.number));
        } else {
          currentDate = new Date(currentDate.getTime() + periodInMs);
        }
        
        if (currentDate <= endDate) {
          totalPayments++;
        }
      }
      
      // Ensure paymentsMade doesn't exceed totalPayments
      paymentsMade = Math.min(paymentsMade, totalPayments);
      
      // Calculate amounts
      const amountPaid = paymentsMade * originalAmount;
      const remainingPayments = totalPayments - paymentsMade;
      const remainingAmount = remainingPayments * amount;
      const totalAmount = amountPaid + remainingAmount;
      
      return {
        totalAmount,
        amountPaid,
        remainingAmount,
        paymentsMade,
        totalPayments,
        nextPaymentDate: nextPaymentDate === "COMPLETED" || (nextPaymentDate instanceof Date && nextPaymentDate > endDate) ? "COMPLETED" : nextPaymentDate,
        originalAmount
      };
    }
    
    // For expenses without end date
    const amountPaid = paymentsMade * originalAmount;
    
    return {
      totalAmount: null, // No fixed total for ongoing payments
      amountPaid,
      remainingAmount: null, // No fixed remaining for ongoing payments
      paymentsMade,
      totalPayments: null, // No fixed total for ongoing payments
      nextPaymentDate,
      originalAmount
    };
  };

  const ExpenseGrid = ({ expenses }) => {
    // Add state for collapsible sections
    const [isSubscriptionExpanded, setIsSubscriptionExpanded] = useState(true);
    const [isFixedTermExpanded, setIsFixedTermExpanded] = useState(true);
    const [isOneOffExpanded, setIsOneOffExpanded] = useState(true);

    // Group expenses by type
    const groupedExpenses = expenses.reduce((acc, expense) => {
      if (expense.isOneOff) {
        acc.oneOff.push(expense);
      } else if (expense.endDate) {
        acc.fixedTerm.push(expense);
      } else {
        acc.subscription.push(expense);
      }
      return acc;
    }, { oneOff: [], fixedTerm: [], subscription: [] });

    // Helper function to determine expense type and get styling
    const getExpenseTypeStyles = (expense) => {
      if (expense.isOneOff) {
        return {
          containerClass: "border-orange-200 dark:border-orange-800",
          badge: {
            text: "One-off Payment",
            class: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
          },
          headerBg: "bg-orange-50 dark:bg-orange-900/20"
        };
      } else if (expense.endDate) {
        return {
          containerClass: "border-blue-200 dark:border-blue-800",
          badge: {
            text: "Fixed Term",
            class: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
          },
          headerBg: "bg-blue-50 dark:bg-blue-900/20"
        };
      } else {
        return {
          containerClass: "border-purple-200 dark:border-purple-800",
          badge: {
            text: "Subscription",
            class: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
          },
          headerBg: "bg-purple-50 dark:bg-purple-900/20"
        };
      }
    };

    const tooltipContent = {
      subscription: "A recurring payment with no end date. Examples: Netflix, Spotify, gym membership.",
      fixedTerm: "A recurring payment with a specific end date. Examples: Car loan, fixed-term insurance, installment plans.",
      oneOff: "A single future payment scheduled in advance. Examples: Pre-booked tickets, scheduled maintenance."
    };

    return (
      <div className="space-y-4">
        {/* Subscriptions Section */}
        {groupedExpenses.subscription.length > 0 && (
          <div className="border rounded-lg bg-white dark:bg-gray-800 shadow-sm">
            <button
              onClick={() => setIsSubscriptionExpanded(!isSubscriptionExpanded)}
              className="w-full p-4 flex justify-between items-center text-left"
            >
              <div className="flex items-center gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-purple-600 dark:text-purple-400">Subscriptions</h3>
                    <FaInfoCircle 
                      className="text-gray-400 hover:text-gray-600 cursor-help"
                      data-tooltip-id="subscription-tooltip"
                    />
                    <Tooltip
                      id="subscription-tooltip"
                      place="right"
                      content={tooltipContent.subscription}
                      className="max-w-xs z-50 dark:bg-gray-100 dark:text-gray-700"
                    />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {groupedExpenses.subscription.length} subscriptions
                  </p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 transform transition-transform duration-300 ${
                  isSubscriptionExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                isSubscriptionExpanded ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-4 pt-0">
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {groupedExpenses.subscription.map(expense => {
                    const paymentDetails = calculatePaymentDetails(expense);
                    const styles = getExpenseTypeStyles(expense);
                    
                    return (
                      <div
                        key={expense.id}
                        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${styles.containerClass} border-2`}
                      >
                        {/* Header section with new styling */}
                        <div className={`${styles.headerBg} p-4`}>
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-lg">{expense.title}</h3>
                            <div className="flex gap-2">
                              {isExpenseCompleted(expense) ? (
                                <button
                                  onClick={() => handleDeleteConfirm(expense)}
                                  className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                >
                                  <FiTrash2 />
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEdit(expense)}
                                    className="text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                                  >
                                    <FiEdit2 />
                                  </button>
                                  {!expense.isOneOff && (
                                    <button
                                      onClick={() => handlePauseConfirm(expense)}
                                      className="text-gray-600 hover:text-yellow-600 dark:text-gray-400 dark:hover:text-yellow-400"
                                    >
                                      {expense.isPaused ? <FiPlay /> : <FiPause />}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteConfirm(expense)}
                                    className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                  >
                                    <FiTrash2 />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Type badge */}
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${styles.badge.class}`}>
                            {styles.badge.text}
                          </span>
                        </div>

                        <div className="p-4">
                          {/* Basic info section */}
                          <div className="space-y-2 mb-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex justify-between">
                              <span>Amount:</span>
                              <span className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.amount}</span>
                            </div>
                            {!expense.isOneOff && (
                              <div className="flex justify-between">
                                <span>Frequency:</span>
                                <span>Every {expense.period.number} {expense.period.unit}{expense.period.number > 1 ? 's' : ''}</span>
                              </div>
                            )}
                            {expense.category && (
                              <div className="flex justify-between">
                                <span>Category:</span>
                                <span>{expense.category}</span>
                              </div>
                            )}
                          </div>

                          {/* Payment details section */}
                          <div className="border-t dark:border-gray-700 pt-3 mt-3">
                            <div className="space-y-2 text-sm">
                              {expense.endDate || paymentDetails.isOneOff ? (
                                <>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className={`${styles.headerBg} p-2 rounded`}>
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Amount</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.totalAmount?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Paid So Far</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.amountPaid?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-700/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Remaining</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.remainingAmount?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-700/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Payments made</p>
                                      <p className="font-medium">
                                        {paymentDetails.paymentsMade} of {paymentDetails.totalPayments} {paymentDetails.isOneOff ? 'payment' : 'payments'}
                                      </p>
                                    </div>
                                  </div>
                                  {expense.originalAmount && expense.originalAmount !== expense.amount && (
                                    <p className="text-xs text-gray-500">
                                      Original amount: {CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.originalAmount} 
                                      &nbsp;(changed to {CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.amount})
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className={`${styles.headerBg} p-3 rounded`}>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">Total Paid So Far</p>
                                    <p className="font-medium text-lg">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.amountPaid?.toFixed(2) || 'N/A'}</p>
                                  </div>
                                </>
                              )}
                              
                              {/* Dates section */}
                              <div className="text-xs text-gray-500 space-y-1 mt-2">
                                <p>Start date: {new Date(expense.startDate).toLocaleDateString()}</p>
                                {paymentDetails.nextPaymentDate ? (
                                  paymentDetails.nextPaymentDate === "COMPLETED" || expense.isCompleted ? (
                                    <p>Status: Completed</p>
                                  ) : (
                                    <p>Next payment: {paymentDetails.nextPaymentDate instanceof Date ? paymentDetails.nextPaymentDate.toLocaleDateString() : "N/A"}</p>
                                  )
                                ) : (
                                  <p>Status: Completed</p>
                                )}
                                {expense.endDate && (
                                  <p>End date: {new Date(expense.endDate).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status badges */}
                          <div className="mt-3">
                            {expense.isPaused && !isExpenseCompleted(expense) && (
                              <span className="inline-block bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-2 py-1 rounded-full text-xs">
                                Paused
                              </span>
                            )}
                            {isExpenseCompleted(expense) && (
                              <span className="inline-block bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full text-xs">
                                Completed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fixed Term Section */}
        {groupedExpenses.fixedTerm.length > 0 && (
          <div className="border rounded-lg bg-white dark:bg-gray-800 shadow-sm">
            <button
              onClick={() => setIsFixedTermExpanded(!isFixedTermExpanded)}
              className="w-full p-4 flex justify-between items-center text-left"
            >
              <div className="flex items-center gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400">Fixed Term Payments</h3>
                    <FaInfoCircle 
                      className="text-gray-400 hover:text-gray-600 cursor-help"
                      data-tooltip-id="fixed-term-tooltip"
                    />
                    <Tooltip
                      id="fixed-term-tooltip"
                      place="right"
                      content={tooltipContent.fixedTerm}
                      className="max-w-xs z-50 dark:bg-gray-100 dark:text-gray-700"
                    />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {groupedExpenses.fixedTerm.length} fixed term payments
                  </p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 transform transition-transform duration-300 ${
                  isFixedTermExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                isFixedTermExpanded ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-4 pt-0">
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {groupedExpenses.fixedTerm.map(expense => {
                    const paymentDetails = calculatePaymentDetails(expense);
                    const styles = getExpenseTypeStyles(expense);
                    
                    return (
                      <div
                        key={expense.id}
                        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${styles.containerClass} border-2`}
                      >
                        {/* Header section with new styling */}
                        <div className={`${styles.headerBg} p-4`}>
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-lg">{expense.title}</h3>
                            <div className="flex gap-2">
                              {isExpenseCompleted(expense) ? (
                                <button
                                  onClick={() => handleDeleteConfirm(expense)}
                                  className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                >
                                  <FiTrash2 />
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEdit(expense)}
                                    className="text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                                  >
                                    <FiEdit2 />
                                  </button>
                                  {!expense.isOneOff && (
                                    <button
                                      onClick={() => handlePauseConfirm(expense)}
                                      className="text-gray-600 hover:text-yellow-600 dark:text-gray-400 dark:hover:text-yellow-400"
                                    >
                                      {expense.isPaused ? <FiPlay /> : <FiPause />}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteConfirm(expense)}
                                    className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                  >
                                    <FiTrash2 />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Type badge */}
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${styles.badge.class}`}>
                            {styles.badge.text}
                          </span>
                        </div>

                        <div className="p-4">
                          {/* Basic info section */}
                          <div className="space-y-2 mb-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex justify-between">
                              <span>Amount:</span>
                              <span className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.amount}</span>
                            </div>
                            {!expense.isOneOff && (
                              <div className="flex justify-between">
                                <span>Frequency:</span>
                                <span>Every {expense.period.number} {expense.period.unit}{expense.period.number > 1 ? 's' : ''}</span>
                              </div>
                            )}
                            {expense.category && (
                              <div className="flex justify-between">
                                <span>Category:</span>
                                <span>{expense.category}</span>
                              </div>
                            )}
                          </div>

                          {/* Payment details section */}
                          <div className="border-t dark:border-gray-700 pt-3 mt-3">
                            <div className="space-y-2 text-sm">
                              {expense.endDate || paymentDetails.isOneOff ? (
                                <>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className={`${styles.headerBg} p-2 rounded`}>
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Amount</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.totalAmount?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Paid So Far</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.amountPaid?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-700/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Remaining</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.remainingAmount?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-700/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Payments made</p>
                                      <p className="font-medium">
                                        {paymentDetails.paymentsMade} of {paymentDetails.totalPayments} {paymentDetails.isOneOff ? 'payment' : 'payments'}
                                      </p>
                                    </div>
                                  </div>
                                  {expense.originalAmount && expense.originalAmount !== expense.amount && (
                                    <p className="text-xs text-gray-500">
                                      Original amount: {CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.originalAmount} 
                                      &nbsp;(changed to {CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.amount})
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className={`${styles.headerBg} p-3 rounded`}>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">Total Paid So Far</p>
                                    <p className="font-medium text-lg">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.amountPaid?.toFixed(2) || 'N/A'}</p>
                                  </div>
                                </>
                              )}
                              
                              {/* Dates section */}
                              <div className="text-xs text-gray-500 space-y-1 mt-2">
                                <p>Start date: {new Date(expense.startDate).toLocaleDateString()}</p>
                                {paymentDetails.nextPaymentDate ? (
                                  paymentDetails.nextPaymentDate === "COMPLETED" || expense.isCompleted ? (
                                    <p>Status: Completed</p>
                                  ) : (
                                    <p>Next payment: {paymentDetails.nextPaymentDate instanceof Date ? paymentDetails.nextPaymentDate.toLocaleDateString() : "N/A"}</p>
                                  )
                                ) : (
                                  <p>Status: Completed</p>
                                )}
                                {expense.endDate && (
                                  <p>End date: {new Date(expense.endDate).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status badges */}
                          <div className="mt-3">
                            {expense.isPaused && !isExpenseCompleted(expense) && (
                              <span className="inline-block bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-2 py-1 rounded-full text-xs">
                                Paused
                              </span>
                            )}
                            {isExpenseCompleted(expense) && (
                              <span className="inline-block bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full text-xs">
                                Completed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* One-off Section */}
        {groupedExpenses.oneOff.length > 0 && (
          <div className="border rounded-lg bg-white dark:bg-gray-800 shadow-sm">
            <button
              onClick={() => setIsOneOffExpanded(!isOneOffExpanded)}
              className="w-full p-4 flex justify-between items-center text-left"
            >
              <div className="flex items-center gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-orange-600 dark:text-orange-400">One-off Payments</h3>
                    <FaInfoCircle 
                      className="text-gray-400 hover:text-gray-600 cursor-help"
                      data-tooltip-id="one-off-tooltip"
                    />
                    <Tooltip
                      id="one-off-tooltip"
                      place="right"
                      content={tooltipContent.oneOff}
                      className="max-w-xs z-50 dark:bg-gray-100 dark:text-gray-700"
                    />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {groupedExpenses.oneOff.length} one-off payments
                  </p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 transform transition-transform duration-300 ${
                  isOneOffExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                isOneOffExpanded ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-4 pt-0">
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {groupedExpenses.oneOff.map(expense => {
                    const paymentDetails = calculatePaymentDetails(expense);
                    const styles = getExpenseTypeStyles(expense);
                    
                    return (
                      <div
                        key={expense.id}
                        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${styles.containerClass} border-2`}
                      >
                        {/* Header section with new styling */}
                        <div className={`${styles.headerBg} p-4`}>
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-lg">{expense.title}</h3>
                            <div className="flex gap-2">
                              {isExpenseCompleted(expense) ? (
                                <button
                                  onClick={() => handleDeleteConfirm(expense)}
                                  className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                >
                                  <FiTrash2 />
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEdit(expense)}
                                    className="text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                                  >
                                    <FiEdit2 />
                                  </button>
                                  {!expense.isOneOff && (
                                    <button
                                      onClick={() => handlePauseConfirm(expense)}
                                      className="text-gray-600 hover:text-yellow-600 dark:text-gray-400 dark:hover:text-yellow-400"
                                    >
                                      {expense.isPaused ? <FiPlay /> : <FiPause />}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteConfirm(expense)}
                                    className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                  >
                                    <FiTrash2 />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Type badge */}
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${styles.badge.class}`}>
                            {styles.badge.text}
                          </span>
                        </div>

                        <div className="p-4">
                          {/* Basic info section */}
                          <div className="space-y-2 mb-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex justify-between">
                              <span>Amount:</span>
                              <span className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.amount}</span>
                            </div>
                            {!expense.isOneOff && (
                              <div className="flex justify-between">
                                <span>Frequency:</span>
                                <span>Every {expense.period.number} {expense.period.unit}{expense.period.number > 1 ? 's' : ''}</span>
                              </div>
                            )}
                            {expense.category && (
                              <div className="flex justify-between">
                                <span>Category:</span>
                                <span>{expense.category}</span>
                              </div>
                            )}
                          </div>

                          {/* Payment details section */}
                          <div className="border-t dark:border-gray-700 pt-3 mt-3">
                            <div className="space-y-2 text-sm">
                              {expense.endDate || paymentDetails.isOneOff ? (
                                <>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className={`${styles.headerBg} p-2 rounded`}>
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Amount</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.totalAmount?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Paid So Far</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.amountPaid?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-700/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Remaining</p>
                                      <p className="font-medium">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.remainingAmount?.toFixed(2) || 'N/A'}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-700/20 p-2 rounded">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">Payments made</p>
                                      <p className="font-medium">
                                        {paymentDetails.paymentsMade} of {paymentDetails.totalPayments} {paymentDetails.isOneOff ? 'payment' : 'payments'}
                                      </p>
                                    </div>
                                  </div>
                                  {expense.originalAmount && expense.originalAmount !== expense.amount && (
                                    <p className="text-xs text-gray-500">
                                      Original amount: {CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.originalAmount} 
                                      &nbsp;(changed to {CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.amount})
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className={`${styles.headerBg} p-3 rounded`}>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">Total Paid So Far</p>
                                    <p className="font-medium text-lg">{CURRENCY_SYMBOLS_TEXT[expense.currency]}{paymentDetails.amountPaid?.toFixed(2) || 'N/A'}</p>
                                  </div>
                                </>
                              )}
                              
                              {/* Dates section */}
                              <div className="text-xs text-gray-500 space-y-1 mt-2">
                                <p>Start date: {new Date(expense.startDate).toLocaleDateString()}</p>
                                {paymentDetails.nextPaymentDate ? (
                                  paymentDetails.nextPaymentDate === "COMPLETED" || expense.isCompleted ? (
                                    <p>Status: Completed</p>
                                  ) : (
                                    <p>Next payment: {paymentDetails.nextPaymentDate instanceof Date ? paymentDetails.nextPaymentDate.toLocaleDateString() : "N/A"}</p>
                                  )
                                ) : (
                                  <p>Status: Completed</p>
                                )}
                                {expense.endDate && (
                                  <p>End date: {new Date(expense.endDate).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status badges */}
                          <div className="mt-3">
                            {expense.isPaused && !isExpenseCompleted(expense) && (
                              <span className="inline-block bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-2 py-1 rounded-full text-xs">
                                Paused
                              </span>
                            )}
                            {isExpenseCompleted(expense) && (
                              <span className="inline-block bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full text-xs">
                                Completed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show message if no expenses */}
        {expenses.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No expenses in this category.
          </div>
        )}
      </div>
    );
  };

  const isExpenseCompleted = (expense) => {
    if (!expense.endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(expense.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate <= today;
  };

  const togglePause = async (expense) => {
    if (expense.isPaused) {
      // When resuming, show RecurringModal with today's date
      setSelectedExpense({
        ...expense,
        startDate: getTodayDate() // Set default start date to today
      });
      setIsModalOpen(true);
    } else {
      // When pausing, show confirmation modal
      setSelectedExpense(expense);
      setConfirmAction('pause');
      setIsConfirmModalOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
              Recurring Payments
            </h1>
            <p className="mt-1 text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Simplify your finances by managing all your recurring payments in one place
            </p>
          </motion.div>

          <button
            onClick={() => {
              setSelectedExpense(null);
              setIsModalOpen(true);
            }}
            className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Add New Payments
          </button>
        </div>

        {/* Update the view selector */}
        <div className="flex justify-center mb-6 overflow-x-auto">
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex gap-1 min-w-full sm:min-w-0">
            <button
              onClick={() => setCurrentView('active')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                currentView === 'active'
                  ? 'bg-pink-600 text-white'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Active ({activeExpenses.length})
            </button>
            <button
              onClick={() => setCurrentView('paused')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                currentView === 'paused'
                  ? 'bg-yellow-600 text-white'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Paused ({pausedExpenses.length})
            </button>
            <button
              onClick={() => setCurrentView('completed')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                currentView === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Completed ({completedExpenses.length})
            </button>
          </div>
        </div>

        {/* Swipeable Views Container */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ duration: 0.3 }}
          >
            {currentView === 'active' && (
              <div className="border rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="p-4">
                  <h2 className="text-xl font-semibold mb-4">Active Recurring Payments</h2>
                  {activeExpenses.length > 0 ? (
                    <ExpenseGrid expenses={activeExpenses} />
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No active recurring expenses. Click "Add New" to get started.
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentView === 'paused' && (
              <div className="border rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="p-4">
                  <h2 className="text-xl font-semibold mb-4">Paused Recurring Payments</h2>
                  {pausedExpenses.length > 0 ? (
                    <ExpenseGrid expenses={pausedExpenses} />
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No paused recurring expenses.
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentView === 'completed' && (
              <div className="border rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="p-4">
                  <h2 className="text-xl font-semibold mb-4">Completed Recurring Payments</h2>
                  {completedExpenses.length > 0 ? (
                    <ExpenseGrid expenses={completedExpenses} />
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No completed recurring expenses.
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <RecurringModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedExpense(null);
          }}
          expense={selectedExpense}
          onSuccess={() => {
            setIsModalOpen(false);
            fetchRecurringExpenses();
          }}
          categories={categories}
          updateCategories={updateCategories}
          currencies={userCurrencies}
        />

        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => {
            setIsConfirmModalOpen(false);
            setSelectedExpense(null);
          }}
          onConfirm={handleConfirmAction}
          title={`Confirm ${confirmAction === 'delete' ? 'Delete' : 'Pause'}`}
          message={`Are you sure you want to ${confirmAction} this recurring expense?`}
        />
      </div>
    </div>
  );
};

export default Recurring;

